import axios from "axios";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/jobs";

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useJobApplications = (targetCompanyId) => {
  // Helper to append companyId query param for admin usage
  const adminParam = (params) => {
    if (targetCompanyId) {
      params = params || new URLSearchParams();
      params.append("companyId", targetCompanyId);
    }
    return params;
  };

  const buildQuery = (params) => {
    const str = params ? params.toString() : "";
    return str ? `?${str}` : "";
  };

  // ─── Applications ─────────────────────────────────────────

  const getApplications = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.jobListingId)
        params.append("jobListingId", filters.jobListingId);
      if (filters.search) params.append("search", filters.search);
      adminParam(params);

      const response = await api.get(`/applications${buildQuery(params)}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching applications:", error);
      throw error;
    }
  };

  const getApplicationStats = async () => {
    try {
      const params = adminParam(null);
      const response = await api.get(
        `/applications/stats${buildQuery(params)}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching application stats:", error);
      throw error;
    }
  };

  const getApplicationById = async (id) => {
    try {
      const params = adminParam(null);
      const response = await api.get(
        `/applications/${id}${buildQuery(params)}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching application ${id}:`, error);
      throw error;
    }
  };

  const updateApplication = async (id, updates) => {
    try {
      const params = adminParam(null);
      const response = await api.patch(
        `/applications/${id}${buildQuery(params)}`,
        updates,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating application ${id}:`, error);
      throw error;
    }
  };

  const deleteApplication = async (id) => {
    try {
      const params = adminParam(null);
      await api.delete(`/applications/${id}${buildQuery(params)}`);
    } catch (error) {
      console.error(`Error deleting application ${id}:`, error);
      throw error;
    }
  };

  const exportApplications = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.jobListingId)
        params.append("jobListingId", filters.jobListingId);
      if (filters.search) params.append("search", filters.search);
      adminParam(params);

      const response = await api.get(
        `/applications/export${buildQuery(params)}`,
        { responseType: "blob" },
      );

      // Download as file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `sollicitaties_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting applications:", error);
      throw error;
    }
  };

  // ─── Job Listings ─────────────────────────────────────────

  const getListings = async () => {
    try {
      const params = adminParam(null);
      const response = await api.get(`/company-listings${buildQuery(params)}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching job listings:", error);
      throw error;
    }
  };

  const createListing = async (data) => {
    try {
      const params = adminParam(null);
      const response = await api.post(
        `/company-listings${buildQuery(params)}`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating job listing:", error);
      throw error;
    }
  };

  const updateListing = async (id, data) => {
    try {
      const params = adminParam(null);
      const response = await api.put(
        `/company-listings/${id}${buildQuery(params)}`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating listing ${id}:`, error);
      throw error;
    }
  };

  const deleteListing = async (id) => {
    try {
      const params = adminParam(null);
      await api.delete(`/company-listings/${id}${buildQuery(params)}`);
    } catch (error) {
      console.error(`Error deleting listing ${id}:`, error);
      throw error;
    }
  };

  return {
    // Applications
    getApplications,
    getApplicationStats,
    getApplicationById,
    updateApplication,
    deleteApplication,
    exportApplications,
    // Listings
    getListings,
    createListing,
    updateListing,
    deleteListing,
  };
};

export default useJobApplications;
